import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success:
          'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 [a&]:hover:bg-emerald-500/20 dark:[a&]:hover:bg-emerald-500/20',
        neutral:
          'border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-200 [a&]:hover:bg-slate-500/20 dark:[a&]:hover:bg-slate-500/20'
      },
      size: {
        default: 'px-2 py-0.5 text-xs gap-1 [&>svg]:size-3',
        sm: 'px-2 py-0.5 text-xs gap-1 [&>svg]:size-3',
        md: 'px-3 py-1 text-xs gap-2 [&>svg]:size-3.5',
        lg: 'px-4 py-1.5 text-sm gap-2 [&>svg]:size-4'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
